import React, { forwardRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageBackground, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { apiConfig, Theme } from '@constants';

import { isDisplayable } from '../../../helpers/StringUtils';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'continueWatchingTile';

const ContinueWatchingTile = memo(({ data, focused = false }) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });
  const widthProgressBar = data?.progress ? data.progress * 100 : 0;
  const season = data?.channel?.season;
  const episode = data.episode;
  const timeLeft = TimeUtils.episodeTimeLeft(data?.duration, data?.progress);
  // right now we're getting back video thumbnails instead of portrait show tiles
  const imageURL = `${apiConfig.DSP_IMAGE_HOST_URL}/${data?.thumb}`;

  return (
    <View style={focused ? styles.focused : styles.container}>
      <ImageBackground source={{ uri: imageURL }} style={styles.thumbnail}>
        <View style={styles.textContainer}>
          <TWCText noTestID style={styles.text}>
            {isDisplayable(season) && isDisplayable(episode)
              ? `S${season}:E${episode}`
              : isDisplayable(episode)
              ? `E${episode}`
              : ''}
          </TWCText>
          <TWCText noTestID fontFamily={'light'} style={styles.text}>
            {timeLeft}
            {` ${t('timeLeftText')}`}
          </TWCText>
        </View>
      </ImageBackground>
      <View style={styles.progressWrapper}>
        <View style={[styles.progress, { width: `${widthProgressBar}%` }]} />
      </View>
    </View>
  );
});

export const OnDemandContinueWatchingTile = forwardRef(
  (
    {
      accessibilityLabel,
      accessibilityHint,
      data,
      testID,
      onPress,
      nextFocusUp,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      onFocus,
    },
    reference,
  ) => {
    if (!data) {
      return <></>;
    }

    return (
      <FocusableHighlight
        accessibilityHint={accessibilityHint}
        accessibilityLabel={accessibilityLabel}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        testID={testID}
        underlayColor={Theme.transparent}
        onFocus={onFocus}
        onPress={onPress}
      >
        <ContinueWatchingTile data={data} />
      </FocusableHighlight>
    );
  },
);

OnDemandContinueWatchingTile.propTypes = {
  accessibilityLabel: PropTypes.string,
  accessibilityHint: PropTypes.string,
  data: PropTypes.object,
  testID: PropTypes.string,
  onPress: PropTypes.func,
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onFocus: PropTypes.func,
};

OnDemandContinueWatchingTile.defaultProps = {
  accessibilityLabel: null,
  accessibilityHint: null,
  data: null,
  testID: null,
  onPress: () => {},
  nextFocusUp: null,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  onFocus: () => {},
};

OnDemandContinueWatchingTile.displayName = 'OnDemandContinueWatching';

const SIZES = {
  borderWidth: 6,
  componentWidth: 540,
  componentHeight: 360,
  barHeight: 13,
};

const styles = StyleSheet.create({
  container: {
    alignContent: 'center',
    borderColor: Theme.transparent,
    borderRadius: Theme.tileBorderRadius,
    borderWidth: Theme.px(SIZES.borderWidth),
    height: Theme.px(SIZES.componentHeight + SIZES.borderWidth * 2),
    overflow: 'hidden',
    width: Theme.px(SIZES.componentWidth + SIZES.borderWidth * 2),
  },
  focused: {
    alignContent: 'center',
    borderColor: Theme.whisperWhite,
    borderRadius: Theme.tileBorderRadius,
    borderWidth: Theme.px(SIZES.borderWidth),
    height: Theme.px(SIZES.componentHeight + SIZES.borderWidth * 2),
    overflow: 'hidden',
    width: Theme.px(SIZES.componentWidth + SIZES.borderWidth * 2),
  },
  progress: {
    backgroundColor: Theme.dodgerLightBlue,
    height: Theme.px(SIZES.barHeight),
    width: Theme.px(SIZES.componentWidth),
  },
  progressWrapper: {
    backgroundColor: Theme.neroBlack,
    height: Theme.px(13),
    overflow: 'hidden',
  },
  text: {
    color: Theme.whisperWhite,
    fontSize: Theme.px(32),
    fontWeight: '200',
  },
  textContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.px(14),
    paddingVertical: Theme.px(8),
  },
  thumbnail: {
    height: Theme.px(SIZES.componentHeight - SIZES.barHeight),
    justifyContent: 'flex-end',
    width: Theme.px(SIZES.componentWidth),
  },
});
